import { Box, Typography, Paper, Grid, Button, Select, MenuItem, FormControl, InputLabel, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Utility } from "../../../../utils/Utility";
import { DeviceDetailInfoGridProps } from "./DeviceDetailInfoGrid";
import { DeviceUiConverter } from "../../../../converter/DeviceUiConverter";
import SimTypeBox from "../../box/SimTypeBox";
import { useState, useRef, useEffect } from "react";
import { subMonths, startOfMonth, format } from "date-fns";
import { useFormContext } from "react-hook-form";
import { DeviceDetailRequest } from "../../../terminal/api/TerminalForm";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ByteFormatter from "../../../../utils/formatter/ByteFormatter";

// テキストが省略されているか判定してツールチップを表示するコンポーネント
const EllipsisTooltip: React.FC<{ value: string; color?: string; isBold?: boolean }> = ({ value, color, isBold = false }) => {
  const textRef = useRef<HTMLElement>(null);
  const [isOverflowed, setIsOverflowed] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      const element = textRef.current;
      if (element) {
        setIsOverflowed(element.scrollWidth > element.clientWidth);
      }
    };
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
  return () => window.removeEventListener('resize', checkOverflow);
  }, [value]);

  const baseStyle = {
    fontSize: "0.75rem",
    fontWeight: isBold ? 700 : 400,
    fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
    color: color || "#333",
    flex: 1,
    padding: "2px 4px",
    display: 'block',
    borderBottom: 'none',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  return (
    <Tooltip title={isOverflowed ? value : ''} arrow>
      <Typography ref={textRef} sx={baseStyle}>
        {value}
      </Typography>
    </Tooltip>
  );
};

// テーブルセル用：テキストが省略されているか判定してツールチップを表示するコンポーネント
const TableCellTooltip: React.FC<{ value: string, enableLineBreak?: boolean }> = ({ value, enableLineBreak = false }) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isOverflowed, setIsOverflowed] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      const element = textRef.current;
      if (element) {
        setIsOverflowed(element.scrollWidth > element.clientWidth);
      }
    };
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [value]);

  return (
    <Tooltip title={isOverflowed ? value : ''} arrow>
      <span ref={textRef} style={{ 
        display: 'block', 
        overflow: 'hidden', 
        textOverflow: 'ellipsis', 
        whiteSpace: enableLineBreak ? 'normal' : 'nowrap',
        fontSize: "0.75rem",
        fontWeight: 400,
        fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
        color: "#333",
      }}>
        {!enableLineBreak ? value : <pre>
          {value}
        </pre>}
      </span>
    </Tooltip>
  );
};

const style = {
  sectionBox: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    paddingLeft: "24px",
    paddingRight: "24px",
    paddingBottom: "16px",
    paddingTop: "6px",
    marginTop: "-10px",
    marginBottom: "10px",
    display: "flex",
  },
  contentArea: {
    flex: 1,
    paddingLeft: "0px",
    minWidth: 0,
  },
  categoryTitle: {
    fontSize: "1.125rem",
    fontWeight: 600,
    fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
    color: "#333",
    marginBottom: "8px",
  },
  subCategoryTitle: {
    fontSize: "0.875rem",
    backgroundColor: '#e7f2ff',
    px: 1,
    paddingTop: 1,
    paddingBottom: '-1px',
    minHeight: '28px', maxHeight: '28px', height: '28px',
    fontWeight: 550,
    fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
    color: "#333",
    marginBottom: "8px",
  },
  itemBox: {
    paddingTop: "4px",
    paddingBottom: "4px",
    borderBottom: '1px solid #ccc',
    display: 'flex',
    alignItems: 'center',
    gap: '1em',
    minHeight: '28px',
    maxHeight: '28px',
    overflow: 'hidden',
    lineHeight: '20px',
  },
  itemLabel: {
    width: "119px",
    fontSize: "0.8rem",
    fontWeight: 500,
    fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
    color: "#666",
    padding: "2px 8px",
    flexShrink: 0,
    display: 'inline-block',
    borderBottom: 'none',
    justifyContent: 'center',
  },
  itemValue: {
    fontSize: "0.8rem",
    fontWeight: 500,
    fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
    color: "#333",
    flex: 1,
    padding: "2px 0px 2px 8px",
    display: 'inline-block',
    borderBottom: 'none',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '& > div': {
      height: '20px',
      display: 'flex',
      alignItems: 'center',
    },
    '& img': {
      maxHeight: '20px',
    },
    justifyContent: 'center',
  },
};

const DeviceDetailInfoGridForFS010C: React.FC<DeviceDetailInfoGridProps> = ({
  contentColor = "#16191f",
  contentFontSize = "0.95rem",
  device,
}) => {
  const navigate = useNavigate();
  const signalDetail = device.signalDetail ? JSON.parse(device.signalDetail) : {};

  const {
    getValues,
    register,
  } = useFormContext<DeviceDetailRequest>();

  const now = new Date();
  const simReplaceLogOptions = Array.from({ length: 12 }, (_, index) => ({
    key: subMonths(startOfMonth(now), index).toISOString(),
    value: <span style={{ fontSize: "0.8rem" }}>{format(subMonths(startOfMonth(now), index), "yyyy年MM月")}</span>,
  }));

  // 確認済みフラグ: modelNameが存在する場合は登録確認済み
  const isDeviceRegistered = device.modelName && device.modelName.trim() !== '';

  return (
    <Box sx={{ width: "100%", minHeight: '28px', maxHeight: '28px' }}>
      <Grid container spacing={2} sx={{
        flexDirection: { xs: 'column', lg: 'row' },
        '& > .MuiGrid-item': {
          maxWidth: { xs: 'none', lg: '50%' }
        }
      }}>
        {/* 基本情報 */}
        <Grid item lg={6}>
          <Paper sx={style.sectionBox} elevation={0}>
            <Box sx={style.contentArea}>
              <Typography sx={style.categoryTitle}>基本情報</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography sx={{ ...style.subCategoryTitle}}>端末情報</Typography>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em', marginTop: '-8px'}}>
                    <Typography sx={style.itemLabel}>IMEI</Typography>
                    <EllipsisTooltip value={device.imei} />
                  </Box>
                  {isDeviceRegistered && (
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>機種</Typography>
                    <EllipsisTooltip value={device.modelName} />
                  </Box>
                  )}
                  {isDeviceRegistered && (
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>FW Ver</Typography>
                    <EllipsisTooltip value={device.firmware && device.firmware.trim() !== '' ? device.firmware : '-'} />
                  </Box>
                  )}
                  {isDeviceRegistered && (
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>MACアドレス</Typography>
                    <EllipsisTooltip value={device.macAddress && device.macAddress.trim() !== '' ? device.macAddress : '-'} />
                  </Box>
                  )}
                  {isDeviceRegistered && (
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>EID</Typography>
                    <EllipsisTooltip value={device.eid && device.eid.trim() !== '' ? device.eid : '-'} />
                  </Box>
                  )}
                  {isDeviceRegistered && (
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>デバイス名</Typography>
                    <EllipsisTooltip
                      value={device.deviceName && device.deviceName.trim() !== '' ? device.deviceName : '未設定'}
                      color={!device.deviceName ? '#bbb' : '#333'}
                    />
                  </Box>
                  )}
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>登録日時</Typography>
                    <EllipsisTooltip value={null !== device.createDate ? (Utility.dateFormatYmdHis(device.createDate) || '') : ''} />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ ...style.subCategoryTitle}}>
                    <span style={{ backgroundColor: '#e7f2ff'}}>MDM管理情報</span>
                  </Typography>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em', marginTop: '-8px'}}>
                    <Typography sx={style.itemLabel}>MDM管理</Typography>
                    <EllipsisTooltip value={isDeviceRegistered ? (device.isMdmManaged ? "有効" : "無効") : "登録確認未実施"} />
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>テナント</Typography>
                    <EllipsisTooltip value={device.tenantName} />
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>端末利用者</Typography>
                    <EllipsisTooltip
                      value={device.username && device.username.trim() !== '' ? device.username : '未設定'}
                      color={!device.username ? '#bbb' : '#333'}
                    />
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>タグ</Typography>
                    <Box sx={{ maxWidth: 240, flex: 1, minWidth: 0 }}>
                      <EllipsisTooltip
                        value={device.tag && device.tag.length > 0 ? device.tag.map((t) => t.tagName).join(", ") : '未設定'}
                        color={device.tag && device.tag.length > 0 ? '#333' : '#bbb'}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>ラベル</Typography>
                    <Box sx={{ maxWidth: 240, flex: 1, minWidth: 0 }}>
                      <EllipsisTooltip
                        value={device.label && device.label.trim() !== '' ? device.label : '未設定'}
                        color={!device.label ? '#bbb' : '#333'}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>コメント</Typography>
                    <Box sx={{ maxWidth: 240, flex: 1, minWidth: 0 }}>
                      <EllipsisTooltip
                      value={device.comment && device.comment.trim() !== '' ? device.comment : '未設定'}
                      color={!device.comment ? '#bbb' : '#333'}
                      />
                    </Box>
                  </Box>
                  {isDeviceRegistered && (
                    <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                      <Typography sx={style.itemLabel}>最終通知日時</Typography>
                      <EllipsisTooltip value={null !== device.lastNotificationDate ? (Utility.dateFormatYmdHis(device.lastNotificationDate) || '') : '-'} />
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>

        {/* 端末状態 */}
        <Grid item lg={6}>
          <Paper sx={{ ...style.sectionBox, marginTop: { xs: '-1em', lg: '-10px' } }} elevation={0}>
            <Box sx={style.contentArea}>
              <Typography sx={style.categoryTitle}>端末状態</Typography>
              {isDeviceRegistered ? (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    {/* <Box sx={{}}> */}
                    <Typography sx={{ ...style.subCategoryTitle}}>状態情報</Typography>
                    {/* </Box> */}
                    <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em', marginTop: '-8px' }}>{/*borderTop: '1px solid #ccc' */}
                      <Typography sx={{ ...style.itemLabel }}>SIM/eSIM</Typography>
                      <Box sx={{ ...style.itemValue, marginLeft: "-6px" }}>
                        <SimTypeBox
                          isBold
                          type={device.simType}
                          fontSize={contentFontSize}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                      <Typography sx={style.itemLabel}>電波強度</Typography>
                      <Box sx={{ ...style.itemValue, marginLeft: "-6.5px" }}>
                        {DeviceUiConverter.getSingignalLevelIcon(
                          device.signalLevel,
                          device.signalUpdateDate,
                          device.isOnline,
                          device.modelName,
                          device.networkType
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                      <Typography sx={style.itemLabel}>接続時間</Typography>
                      <EllipsisTooltip value={null !== device.connTime ? (Utility.dateFormatdHis(device.connTime) || '') : '-'} />
                    </Box>
                    <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                      <Typography sx={style.itemLabel}>CPU使用率</Typography>
                      <EllipsisTooltip value={!!device.cpu_use ? device.cpu_use + '%' : '-'} />
                    </Box>
                    <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                      <Typography sx={style.itemLabel}>メモリ使用率</Typography>
                      <EllipsisTooltip value={!!device.mem_use ? device.mem_use + '%' : '-'} />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box>
                      <Typography sx={{ ...style.subCategoryTitle}}>規制状態</Typography>
                    </Box>
                    <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em', marginTop: '-8px'}}>
                      <Typography sx={style.itemLabel}>通信規制</Typography>
                      <EllipsisTooltip value={device.isDataLimit} />
                    </Box>
                    <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                      <Typography sx={style.itemLabel}>ボタン操作ロック</Typography>
                      <Box sx={{ ...style.itemValue, fontWeight: 400, fontSize: '0.85rem'}}>
                        {DeviceUiConverter.getLockIcon(
                          device.buttonLock,
                          contentFontSize
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                      <Typography sx={style.itemLabel}>WebUIロック</Typography>
                      <Box sx={{ ...style.itemValue, fontWeight: 400, fontSize: '0.85rem'}}>
                        {DeviceUiConverter.getLockIcon(
                          device.webuiLock,
                          contentFontSize
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                      <Typography sx={style.itemLabel}>通信ロック</Typography>
                      <Box sx={{ ...style.itemValue, fontWeight: 400, fontSize: '0.85rem'}}>
                        {DeviceUiConverter.getLockIcon(
                          device.connLock,
                          contentFontSize
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                      <Typography sx={style.itemLabel}>MDMロック</Typography>
                      <Box sx={{ ...style.itemValue, fontWeight: 400, fontSize: '0.85rem'}}>
                        {DeviceUiConverter.getLockIcon(
                          device.mdmLock,
                          contentFontSize
                        )}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              ) : (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '24px'
                }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.875rem' }}>
                      登録確認が行われていないため、表示できません。
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem' }}>
                      ※該当端末がインターネット接続できる状態にしてください。
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{
        flexDirection: { xs: 'column', lg: 'row' },
        '& > .MuiGrid-item': {
          maxWidth: { xs: 'none', lg: '50%' }
        }
      }}>
        {/* ネットワーク */}
        <Grid item lg={6}>
          <Paper sx={{ ...style.sectionBox, marginTop: '0' }} elevation={0}>
            <Box sx={style.contentArea}>
              <Typography sx={style.categoryTitle}>ネットワーク</Typography>
              {isDeviceRegistered ? (
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  {/* 通信情報 */}
                  <Typography sx={{ ...style.subCategoryTitle}}>通信情報 ({device.simType})</Typography>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', marginTop: '-8px' }}>
                    <Typography sx={style.itemLabel}>事業者</Typography>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <EllipsisTooltip
                        value={device.carrier && device.carrier.trim() !== '' ? device.carrier : '-'}
                        color={DeviceUiConverter.getCarrierColored(device.carrier, contentColor)}
                        isBold
                      />
                    </Box>
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>電話番号</Typography>
                    <EllipsisTooltip value={device.phoneNumber && device.phoneNumber.trim() !== '' ? device.phoneNumber : '-'} />
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>IMSI</Typography>
                    <EllipsisTooltip value={device.imsi && device.imsi.trim() !== '' ? device.imsi : '-'} />
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>ICCID</Typography>
                    <EllipsisTooltip value={device.iccid && device.iccid.trim() !== '' ? device.iccid : '-'} />
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>ネットワーク</Typography>
                    <EllipsisTooltip value={device.network && device.network.trim() !== '' ? device.network : '-'} />
                  </Box>
                  {device.cellInfo && (
                    <>
                      <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                        <Typography sx={style.itemLabel}>Cell ID</Typography>
                        <EllipsisTooltip value={device.cellInfo.cellId || '-'} />
                      </Box>
                      <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                        <Typography sx={style.itemLabel}>MCC/MNC</Typography>
                        <EllipsisTooltip value={device.cellInfo.mcc && device.cellInfo.mnc ? `${device.cellInfo.mcc}/${device.cellInfo.mnc}` : '-'} />
                      </Box>
                      <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                        <Typography sx={style.itemLabel}>TAC</Typography>
                        <EllipsisTooltip value={device.cellInfo.tac || '-'} />
                      </Box>
                    </>
                  )}
                </Grid>
                <Grid item xs={6}>
                  {/* アドレス情報 */}
                  <Typography sx={{ ...style.subCategoryTitle}}>アドレス情報 </Typography>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em', marginTop: '-8px'}}>
                      <Typography sx={style.itemLabel}>IP（DHCP）</Typography>
                      <EllipsisTooltip value={device.ipAddress && device.ipAddress.trim() !== '' ? device.ipAddress : '-'} />
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>WAN IP</Typography>
                    <EllipsisTooltip value={device.wanIp && device.wanIp.trim() !== '' ? device.wanIp : '-'} />
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>プライマリDNS</Typography>
                    <EllipsisTooltip value={device.dnsP && device.dnsP.trim() !== '' ? device.dnsP : '-'} />
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>セカンダリDNS</Typography>
                    <EllipsisTooltip value={device.dnsS && device.dnsS.trim() !== '' ? device.dnsS : '-'} />
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>WAN IPv6</Typography>
                    <EllipsisTooltip value={device.wanIp6 && device.wanIp6.trim() !== '' ? device.wanIp6 : '-'} />
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>プライマリIPv6DNS</Typography>
                    <EllipsisTooltip value={device.dns6P && device.dns6P.trim() !== '' ? device.dns6P : '-'} />
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>セカンダリIPv6DNS</Typography>
                    <EllipsisTooltip value={device.dns6S && device.dns6S.trim() !== '' ? device.dns6S : '-'} />
                  </Box>
                </Grid>
              </Grid>
              ) : (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '24px'
                }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.875rem' }}>
                      登録確認が行われていないため、表示できません。
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem' }}>
                      ※該当端末がインターネット接続できる状態にしてください。
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* データ通信量 */}
        <Grid item lg={6}>
          <Paper sx={{ ...style.sectionBox, marginTop: { xs: '-1em', lg: isDeviceRegistered ? '-4.6em' : '-10.6em' } }} elevation={0}>
            <Box sx={style.contentArea}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, height: "27px",}}>
                <Typography sx={{ ...style.categoryTitle }}>データ通信量</Typography>
                <Button
                  sx={{
                    fontSize: "0.8rem",
                    padding: "0 12px",
                    minWidth: "60px",
                    border: '1.5px solid',
                    borderColor: '#1976d2',
                    color: '#1976d2',
                    borderRadius: "18px",
                    background: "#f7fafd",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                    transition: "background 0.2s, border 0.2s",
                    '&:hover': {
                      background: '#e3f2fd',
                    },
                  }}
                  onClick={() => {
                    sessionStorage.setItem("SIM_TYPE", "0");
                    navigate("/terminal_traffic_stats");
                  }}
                >
                  通信量グラフを表示
                  <ArrowForwardIosIcon sx={{ fontSize: "0.95rem", ml: 0.5 }} />
                </Button>
              </Box>
              {isDeviceRegistered ? (
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', color: "#333", fontSize: '0.9em' }}>
                  <thead>
                    <tr style={{...style.subCategoryTitle, background: '#e7f2ff' }}>
                        <th style={{padding: '4px 12px', fontWeight: 500, textAlign: 'left' , minHeight: '28px', maxHeight: '28px', height: '28px'}}>種別</th>
                        <th style={{padding: '4px 12px', fontWeight: 500, textAlign: 'left' }}>累積</th>
                        <th style={{padding: '4px 12px', fontWeight: 500, textAlign: 'left' }}>1か月</th>
                        <th style={{padding: '4px 12px', fontWeight: 500, textAlign: 'left' }}>1日</th>
                    </tr>
                  </thead>
                  <tbody style={{ fontWeight: 400, fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif", fontSize: '0.9em' }}>
                    <tr>
                      <td style={{padding: '0px 0px 2px 8px', borderBottom: '1px solid #ccc', minHeight: '28px', maxHeight: '28px', height: '28px' }}>　SIM</td>
                      <td style={{padding: '4px 12px', borderBottom: '1px solid #ccc', minHeight: '28px', maxHeight: '28px', height: '28px'}}>{ByteFormatter.formatBytes({bytes: (Number(device.simTraffic) || 0) * 1024, unit:"MB", allowHigherUnits: true})}</td>
                      <td style={{padding: '4px 12px', borderBottom: '1px solid #ccc'}}>{ByteFormatter.formatBytes({bytes: (Number(device.simTraffic1m) || 0) * 1024, unit:"MB", allowHigherUnits: true})}</td>
                      <td style={{padding: '4px 12px', borderBottom: '1px solid #ccc'}}>{ByteFormatter.formatBytes({bytes: (Number(device.simTraffic1d) || 0) * 1024, unit:"MB", allowHigherUnits: true})}</td>
                    </tr>
                    <tr>
                      <td style={{padding: '4px 12px', borderBottom: '1px solid #ccc', minHeight: '28px', maxHeight: '28px', height: '28px' }}>&nbsp;eSIM</td>
                      <td style={{padding: '4px 12px', borderBottom: '1px solid #ccc'}}>{ByteFormatter.formatBytes({bytes: (Number(device.esimTraffic) || 0) * 1024, unit:"MB", allowHigherUnits: true})}</td>
                      <td style={{padding: '4px 12px', borderBottom: '1px solid #ccc'}}>{ByteFormatter.formatBytes({bytes: (Number(device.esimTraffic1m) || 0) * 1024, unit:"MB", allowHigherUnits: true})}</td>
                      <td style={{padding: '4px 12px', borderBottom: '1px solid #ccc'}}>{ByteFormatter.formatBytes({bytes: (Number(device.esimTraffic1d) || 0) * 1024, unit:"MB", allowHigherUnits: true})}</td>
                    </tr>
                  </tbody>
                </table>
              </Box>
              ) : (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '24px',
                  marginTop: "-10px",
                }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.875rem' }}>
                      登録確認が行われていないため、表示できません。
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem' }}>
                      ※該当端末がインターネット接続できる状態にしてください。
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{
        flexDirection: { xs: 'column', lg: 'row' },
        '& > .MuiGrid-item': {
          maxWidth: { xs: 'none', lg: '50%' }
        }
      }}>
        {/* SIM差替えログ */}
        <Grid item lg={6}>
          <Paper sx={{ ...style.sectionBox, marginTop: '0' }} elevation={0}>
            <Box sx={style.contentArea}>
              <Typography sx={{...style.categoryTitle}}>SIM差替えログ</Typography>
              {isDeviceRegistered ? (
              <>
              <Grid container spacing={1} sx={{ marginBottom: "8px" }}>
                <Grid item xs={2.5}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ fontSize: "0.75rem" }}>表示期間</InputLabel>
                    <Select
                      defaultValue={getValues("simReplacementLogDateStart")}
                      label="表示期間"
                      sx={{ fontSize: "0.75rem", height: "32px" }}
                      {...register("simReplacementLogDateStart")}
                    >
                      {simReplaceLogOptions.map((option) => (
                        <MenuItem key={option.key} value={option.key} sx={{ fontSize: "0.8rem" }}>
                          {option.value}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={1.5}>
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{ height: "32px", fontSize: "0.8rem", padding: "0 12px", minWidth: "60px" }}
                  >
                    検索
                  </Button>
                </Grid>
              </Grid>
              <Box sx={{overflowX: 'auto', overflowY: 'auto' }}> {/* maxHeight: 600 */}
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em', tableLayout: 'fixed' }}>
                  <thead>
                    <tr style={{...style.subCategoryTitle, background: '#e7f2ff' }}>
                        <th style={{ padding: '4px 12px', fontWeight: 500, textAlign: 'left', minHeight: '28px', maxHeight: '28px', height: '28px' }}>No</th>
                        <th style={{ padding: '4px 12px', fontWeight: 500, textAlign: 'left' }}>差替え日時</th>
                        <th style={{ padding: '4px 12px', fontWeight: 500, textAlign: 'left' }}>タイプ</th>
                        <th style={{ padding: '4px 12px', fontWeight: 500, textAlign: 'left' }}>ICCID</th>
                    </tr>
                  </thead>
                  <tbody style={{ color: "#464545ff", fontWeight: 400, fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif" }}>
                    {Array.from({
                      length:
                        !device.simSwapLogs || device.simSwapLogs.length < 3
                          ? 3
                          : device.simSwapLogs.length === 10
                            ? 10
                            : device.simSwapLogs.length+1
                      }).map((_, index) => {
                        const log = device.simSwapLogs?.[index];
                        return (
                          <tr key={index}>
                            <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', minHeight: '28px', maxHeight: '28px', height: '28px', maxWidth: '50px' }}>
                              <TableCellTooltip value={log ? String(index + 1) : '-'} />
                            </td>
                            <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', maxWidth: '120px' }}>
                              <TableCellTooltip value={log?.swapDate || '-'} />
                            </td>
                            <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', maxWidth: '80px' }}>
                              {log?.simType ? (
                                <SimTypeBox
                                  isBold
                                  type={log.simType}
                                  fontSize={contentFontSize}
                                />
                              ) : '-'}
                            </td>
                            <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', maxWidth: '120px' }}>
                              <TableCellTooltip value={log?.iccid || '-'} />
                            </td>
                          </tr>
                        );
                      })
                    }
                  </tbody>
                </table>
              </Box>
              </>
              ) : (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '24px'
                }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.875rem' }}>
                      登録確認が行われていないため、表示できません。
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem' }}>
                      ※該当端末がインターネット接続できる状態にしてください。
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
        <Grid item lg={6}>
          {/* 無線LAN(Wi-Fi)接続情報 */}
          <Paper sx={{ ...style.sectionBox, marginTop: { xs: '-1em', lg: isDeviceRegistered ? '-16.1em' : '-11.3em' } }} elevation={0}>
            <Box sx={style.contentArea}>
              <Typography sx={{...style.categoryTitle}}>無線LAN(Wi-Fi)接続情報</Typography>
              {isDeviceRegistered ? (
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95em', tableLayout: 'fixed' }}>
                  <thead>
                    <tr style={{...style.subCategoryTitle, background: '#e7f2ff' }}>
                        <th style={{ padding: '4px 12px', fontWeight: 500, textAlign: 'left', minHeight: '28px', maxHeight: '28px', height: '28px' }}>無線LAN(Wi-Fi)</th>
                        <th style={{ padding: '4px 12px', fontWeight: 500, textAlign: 'left' }}>SSID</th>
                        <th style={{ padding: '4px 12px', fontWeight: 500, textAlign: 'left' }}>パスワード</th>
                        <th style={{ padding: '4px 12px', fontWeight: 500, textAlign: 'left' }}>セキュリティプロトコル</th>
                        <th style={{ padding: '4px 12px', fontWeight: 500, textAlign: 'left' }}>状態</th>
                    </tr>
                  </thead>
                  <tbody style={{ color: "#464545ff", fontWeight: 400, fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif" }}>
                    {(device.wifiInfoFS010C)?.map((wifi, index) => (
                      <tr key={`wifiInfo[${index}]`}>
                        <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', minHeight: '28px', maxHeight: '28px', height: '28px', maxWidth: '80px' }}>
                          <TableCellTooltip value={['2.4GHz', '5GHz', 'Guest1（2.4GHz）', 'Guest2（2.4GHz）', 'Guest3（2.4GHz）', 'Guest1（5GHz）', 'Guest2（5GHz）', 'Guest3（5GHz）'][index]} />
                        </td>
                        <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', maxWidth: '100px' }}>
                          <TableCellTooltip value={wifi?.ssid || '-'} />
                        </td>
                        <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', maxWidth: '100px' }}>
                          <TableCellTooltip value={wifi?.passwd || '-'} />
                        </td>
                        <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', maxWidth: '120px' }}>
                          <TableCellTooltip value={wifi?.securityProtocol || '-'} />
                        </td>
                        <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', maxWidth: '80px' }}>
                          <TableCellTooltip value={wifi?.status || '-'} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
              ) : (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '24px'
                }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.875rem' }}>
                      登録確認が行われていないため、表示できません。
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem' }}>
                      ※該当端末がインターネット接続できる状態にしてください。
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{
        flexDirection: { xs: 'column', lg: 'row' },
        '& > .MuiGrid-item': {
          maxWidth: { xs: 'none', lg: '50%' }
        }
      }}>
        {/* 診断 */}
        <Grid item lg={6}>
          <Paper sx={{ ...style.sectionBox, marginTop: '0' }} elevation={0}>
            <Box sx={style.contentArea}>
              <Typography sx={{...style.categoryTitle}}>診断</Typography>
              {device.diagnosis ? (
                <Box sx={{ overflowX: 'auto', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95em', tableLayout: 'fixed' }}>
                    <thead>
                      <tr style={{...style.subCategoryTitle, background: '#e7f2ff' }}>
                        <th style={{ padding: '4px 12px', fontWeight: 500, textAlign: 'left', minHeight: '28px', maxHeight: '28px', height: '28px', width: '10%' }}>項目</th>
                        <th style={{ padding: '4px 12px', fontWeight: 500, textAlign: 'left', minHeight: '28px', maxHeight: '28px', height: '28px', width: '90%' }}>内容</th>
                      </tr>
                    </thead>
                    <tbody style={{ color: "#464545ff", fontWeight: 400, fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif" }}>
                      <tr>
                        <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', minHeight: '28px', maxHeight: '28px', height: '28px' }}>
                          <TableCellTooltip value={'日時'} />
                        </td>
                        <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', minHeight: '28px', maxHeight: '28px', height: '28px' }}>
                          <TableCellTooltip value={device.diagnosis?.date ? Utility.dateFormatYmdHi(device.diagnosis.date) || '-' : '-'} />
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', minHeight: '28px', maxHeight: '28px', height: '28px' }}>
                          <TableCellTooltip value={'タイプ'} />
                        </td>
                        <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', minHeight: '28px', maxHeight: '28px', height: '28px' }}>
                          <TableCellTooltip value={device.diagnosis?.type || '-'} />
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', minHeight: '28px', maxHeight: '28px', height: '28px' }}>
                          <TableCellTooltip value={'結果'} />
                        </td>
                        <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', minHeight: '28px', maxHeight: '28px', height: '28px' }}>
                          <TableCellTooltip value={device.diagnosis?.result == '0' ? '成功' : '失敗'} />
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', minHeight: '28px', maxHeight: '28px', height: '28px' }}>
                          <TableCellTooltip value={'詳細'} />
                        </td>
                        <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', minHeight: '28px', maxHeight: '28px', height: '28px' }}>
                          <TableCellTooltip 
                            value={device.diagnosis?.detail || '-'} 
                            enableLineBreak={true}
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </Box>
              ) : (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '24px'
                }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.875rem' }}>
                      診断結果が存在しないため、表示できません。
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* 接続機器一覧 */}
        <Grid item lg={6}>
          <Paper sx={{ ...style.sectionBox, marginTop: { xs: '-1em', lg: isDeviceRegistered ? '-6.2em' : '-11.3em' } }} elevation={0}>
            <Box sx={style.contentArea}>
              <Typography sx={{...style.categoryTitle}}>接続機器一覧</Typography>
              {isDeviceRegistered ? (
              <Box sx={{overflowX: 'auto', overflowY: 'auto' }}> {/* maxHeight: 600 */}
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95em', tableLayout: 'fixed' }}>
                  <thead>
                    <tr style={{...style.subCategoryTitle, background: '#e7f2ff' }}>
                        <th style={{ padding: '4px 12px', fontWeight: 500, textAlign: 'left', minHeight: '28px', maxHeight: '28px', height: '28px' }}>No</th>
                        <th style={{ padding: '4px 12px', fontWeight: 500, textAlign: 'left' }}>接続機器名</th>
                        <th style={{ padding: '4px 12px', fontWeight: 500, textAlign: 'left' }}>MACアドレス</th>
                        <th style={{ padding: '4px 12px', fontWeight: 500, textAlign: 'left' }}>IPアドレス</th>
                        <th style={{ padding: '4px 12px', fontWeight: 500, textAlign: 'left' }}>接続時間</th>
                        <th style={{ padding: '4px 12px', fontWeight: 500, textAlign: 'left' }}>接続形態</th>
                    </tr>
                  </thead>
                  <tbody style={{ color: "#464545ff", fontWeight: 400, fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif" }}>
                    {Array.from({
                      length:
                        !device.equipments || device.equipments.length < 5
                          ? 5
                          : device.equipments.length
                      }).map((_, index) => {
                      const equipment = device.equipments?.[index];
                        return (
                          <tr key={index}>
                            <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', minHeight: '28px', maxHeight: '28px', height: '28px', maxWidth: '50px' }}>
                              <TableCellTooltip value={equipment ? String(index + 1) : '-'} />
                            </td>
                            <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', minHeight: '28px', maxHeight: '28px', height: '28px', maxWidth: '100px' }}>
                              <TableCellTooltip value={equipment?.name || '-'} />
                            </td>
                            <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', minHeight: '28px', maxHeight: '28px', height: '28px', maxWidth: '120px' }}>
                              <TableCellTooltip value={equipment?.mac || '-'} />
                            </td>
                            <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', minHeight: '28px', maxHeight: '28px', height: '28px', maxWidth: '100px' }}>
                              <TableCellTooltip value={equipment?.ip || '-'} />
                            </td>
                            <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', minHeight: '28px', maxHeight: '28px', height: '28px', maxWidth: '120px' }}>
                              <TableCellTooltip value={equipment?.connectionTime ? Utility.dateFormatdHis(String((parseInt(equipment.connectionTime) ?? 0) * 60)) || '-' : '-'} />
                            </td>
                            <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', minHeight: '28px', maxHeight: '28px', height: '28px', maxWidth: '80px' }}>
                              <TableCellTooltip value={equipment?.form || '-'} />
                            </td>
                          </tr>
                        );
                      })
                    }
                  </tbody>
                </table>
              </Box>
              ) : (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '24px'
                }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.875rem' }}>
                      登録確認が行われていないため、表示できません。
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem' }}>
                      ※該当端末がインターネット接続できる状態にしてください。
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DeviceDetailInfoGridForFS010C;